import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkTheme, setIsDarkTheme] = useState(() => {
    // Get theme from localStorage on initialization
    // Check for both possible keys and clean up old one
    const savedTheme = localStorage.getItem('videoCallTheme');
    const oldTheme = localStorage.getItem('video-call-theme');
    
    // Clean up old key if it exists
    if (oldTheme) {
      localStorage.removeItem('video-call-theme');
    }
    
    return savedTheme === 'dark';
  });

  // Update localStorage and body class when theme changes
  useEffect(() => {
    localStorage.setItem('videoCallTheme', isDarkTheme ? 'dark' : 'light');
    
    // Apply theme to body element - only add .dark class when it's actually dark theme
    if (isDarkTheme) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  }, [isDarkTheme]);

  // Load theme from localStorage on component mount and clean up old keys
  useEffect(() => {
    // Clean up any old theme keys
    localStorage.removeItem('video-call-theme');
    
    const savedTheme = localStorage.getItem('videoCallTheme');
    if (savedTheme === 'dark') {
      setIsDarkTheme(true);
      document.body.classList.add('dark');
    } else {
      setIsDarkTheme(false);
      document.body.classList.remove('dark');
    }
  }, []);

  const toggleTheme = () => {
    setIsDarkTheme(!isDarkTheme);
  };

  const setLightTheme = () => {
    setIsDarkTheme(false);
  };

  const setDarkTheme = () => {
    setIsDarkTheme(true);
  };

  const value = {
    isDarkTheme,
    toggleTheme,
    setLightTheme,
    setDarkTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
